import React, { useState, useEffect } from "react";
import { db, auth } from "../firebase";
import { collection, addDoc, doc, getDocs, updateDoc, Timestamp, query, orderBy, QueryDocumentSnapshot } from "firebase/firestore";
import { toast } from "react-toastify";
import "../styles/Modal.css";
import "../styles/DevisModal.css";
import "../styles/Inter.css";
import { logUserAction } from "../utils/logUtils";

interface AdminUser {
  email: string;
  firstName?: string;
  lastName?: string;
}

interface SubTask {
  id: string;
  title: string;
  completed: boolean;
  assignedTo?: string;
  status?: string;
  completedAt?: Timestamp | null;
  order?: number; // Ordre dans la liste pour le réordonnancement
}

interface Task {
  order?: number;
  id: string;
  title: string;
  description: string;
  status: string;
  priority: string;
  assignedTo?: string;
  dueDate?: Timestamp;
  createdAt: Timestamp;
  updatedAt: Timestamp;
  uid?: string;
  listId?: string;
  completed?: boolean;
  completedItems?: number;
  totalItems?: number;
  subTasks?: SubTask[];
  subTaskMode?: "Collectif" | "Individuel";
}

interface TaskModalProps {
  show: boolean;
  handleClose: () => void;
  initialData?: Task | null;
}

const TaskModal: React.FC<TaskModalProps> = ({ show, handleClose, initialData }) => {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [status, setStatus] = useState("En attente");
  const [priority, setPriority] = useState("Moyenne");
  const [assignedTo, setAssignedTo] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [subTasks, setSubTasks] = useState<SubTask[]>([]);
  const [newSubTaskTitle, setNewSubTaskTitle] = useState("");
  const [newSubTaskAssignedTo, setNewSubTaskAssignedTo] = useState("");
  const [newSubTaskStatus, setNewSubTaskStatus] = useState("En attente");
  const [subTaskMode, setSubTaskMode] = useState<"Collectif" | "Individuel">("Individuel");
  const [showSubTaskTable, setShowSubTaskTable] = useState(false);
  const [adminUsers, setAdminUsers] = useState<AdminUser[]>([]);
  const [draggedSubTask, setDraggedSubTask] = useState<SubTask | null>(null);
  const [draggedOverSubTask, setDraggedOverSubTask] = useState<string | null>(null);

  // Charger la liste des utilisateurs admin
  useEffect(() => {
    const fetchAdminUsers = async () => {
      try {
        const usersSnapshot = await getDocs(collection(db, "Users"));
        const admins: AdminUser[] = [];
        
        usersSnapshot.forEach((docSnap) => {
          const data = docSnap.data();
          if (data.role === "admin") {
            admins.push({
              email: docSnap.id,
              firstName: data.firstName || "",
              lastName: data.lastName || ""
            });
          }
        });
        
        // Trier par prénom/nom
        admins.sort((a, b) => {
          const nameA = `${a.firstName} ${a.lastName}`.trim() || a.email;
          const nameB = `${b.firstName} ${b.lastName}`.trim() || b.email;
          return nameA.localeCompare(nameB);
        });
        
        setAdminUsers(admins);
      } catch (error) {
        console.error("Erreur lors du chargement des utilisateurs admin:", error);
      }
    };
    
    fetchAdminUsers();
  }, []);

  // Réinitialiser les états quand la modale s'ouvre
  useEffect(() => {
    if (show) {
      // Vérifier si c'est une modification (tâche existante avec uid) ou un ajout
      const isEditMode = initialData && initialData.uid;
      
      if (isEditMode && initialData) {
        setTitle(initialData.title || "");
        setDescription(initialData.description || "");
        setStatus(initialData.status || "En attente");
        setPriority(initialData.priority || "Moyenne");
        setAssignedTo(initialData.assignedTo || "");
        // Initialiser l'ordre des sous-tâches si manquant et s'assurer que completed correspond au statut
        const subTasksWithOrder = (initialData.subTasks || []).map((st, index) => {
          const isCompleted = st.status === 'Terminée';
          return {
            ...st,
            order: st.order !== undefined && st.order !== null ? st.order : index,
            completed: isCompleted // S'assurer que completed correspond au statut
          };
        });
        setSubTasks(subTasksWithOrder);
        // Utiliser le mode stocké ou déterminer basé sur les sous-tâches existantes
        let detectedMode: "Collectif" | "Individuel" = "Individuel";
        if (initialData.subTaskMode) {
          detectedMode = initialData.subTaskMode;
          setSubTaskMode(initialData.subTaskMode);
        } else if (initialData.subTasks && initialData.subTasks.length > 0) {
          const hasAssignedSubTasks = initialData.subTasks.some(st => st.assignedTo);
          detectedMode = hasAssignedSubTasks ? "Collectif" : "Individuel";
          setSubTaskMode(detectedMode);
        } else {
          setSubTaskMode("Individuel");
        }
        // Afficher automatiquement le tableau si des sous-tâches existent
        if (initialData.subTasks && initialData.subTasks.length > 0) {
          setShowSubTaskTable(true);
        } else {
          setShowSubTaskTable(false);
        }
        if (initialData.dueDate) {
          const date = initialData.dueDate.toDate();
          setDueDate(date.toISOString().split('T')[0]);
        } else {
          setDueDate("");
        }
      } else {
        // Réinitialiser pour une nouvelle tâche
        setTitle("");
        setDescription("");
        setStatus("En attente");
        setPriority("Moyenne");
        setAssignedTo("");
        setDueDate("");
        setSubTasks([]);
        setSubTaskMode("Individuel");
        setShowSubTaskTable(false);
      }
      setNewSubTaskTitle("");
      setNewSubTaskAssignedTo("");
      setNewSubTaskStatus("En attente");
    }
  }, [show, initialData]);

  // Mettre à jour automatiquement les sous-tâches quand l'assigné principal change en mode Individuel
  useEffect(() => {
    if (subTaskMode === "Individuel" && assignedTo.trim() && subTasks.length > 0) {
      setSubTasks(prevSubTasks => prevSubTasks.map(st => {
        const status = st.status || "En attente";
        return {
          ...st,
          assignedTo: assignedTo.trim(),
          completed: status === 'Terminée' // S'assurer que completed correspond au statut
        };
      }));
    }
  }, [assignedTo, subTaskMode]);

  const formatDateForInput = (date: Date | Timestamp | undefined): string => {
    if (!date) return "";
    if (date instanceof Timestamp) {
      return date.toDate().toISOString().split('T')[0];
    }
    if (date instanceof Date) {
      return date.toISOString().split('T')[0];
    }
    return "";
  };

  const addSubTask = () => {
    if (!newSubTaskTitle.trim()) {
      toast.error("Le titre de la sous-tâche est requis");
      return;
    }
    // Initialiser l'ordre si manquant
    const maxOrder = subTasks.length > 0 
      ? Math.max(...subTasks.map(st => st.order !== undefined && st.order !== null ? st.order : -1))
      : -1;
    const status = newSubTaskStatus || "En attente";
    const isCompleted = status === 'Terminée';
    const newSubTask: SubTask = {
      id: Date.now().toString() + Math.random().toString(36).substring(2, 8),
      title: newSubTaskTitle.trim(),
      completed: isCompleted, // completed selon le statut
      assignedTo: subTaskMode === "Individuel" ? assignedTo.trim() || undefined : (newSubTaskAssignedTo.trim() || undefined),
      status: status, // Toujours définir un statut, même en mode Individuel
      order: maxOrder + 1
    };
    setSubTasks([...subTasks, newSubTask]);
    setNewSubTaskTitle("");
    setNewSubTaskAssignedTo("");
    setNewSubTaskStatus("En attente");
    setNewSubTaskAssignedTo("");
    setNewSubTaskStatus("En attente");
  };

  const toggleSubTask = (subTaskId: string) => {
    setSubTasks(subTasks.map(st => 
      st.id === subTaskId ? { ...st, completed: !st.completed } : st
    ));
  };

  const deleteSubTask = (subTaskId: string) => {
    setSubTasks(subTasks.filter(st => st.id !== subTaskId));
  };

  const updateSubTaskTitle = (subTaskId: string, newTitle: string) => {
    setSubTasks(subTasks.map(st => 
      st.id === subTaskId ? { ...st, title: newTitle } : st
    ));
  };

  const updateSubTaskAssignedTo = (subTaskId: string, assignedTo: string) => {
    setSubTasks(subTasks.map(st => 
      st.id === subTaskId ? { ...st, assignedTo: assignedTo.trim() || undefined } : st
    ));
  };

  const updateSubTaskStatus = (subTaskId: string, status: string) => {
    setSubTasks(subTasks.map(st => {
      if (st.id === subTaskId) {
        const isCompleted = status === 'Terminée';
        return { 
          ...st, 
          status,
          completed: isCompleted, // Mettre à jour completed selon le statut
          completedAt: isCompleted ? (st.completedAt || Timestamp.now()) : null // Mettre à jour completedAt (null si pas terminée)
        };
      }
      return st;
    }));
  };

  // Drag and Drop pour les sous-tâches
  const handleSubTaskDragStart = (e: React.DragEvent, subTask: SubTask) => {
    setDraggedSubTask(subTask);
    e.dataTransfer.effectAllowed = "move";
  };

  const handleSubTaskDragOver = (e: React.DragEvent, subTaskId: string) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = "move";
    setDraggedOverSubTask(subTaskId);
  };

  const handleSubTaskDragLeave = (e: React.DragEvent) => {
    e.stopPropagation();
    if (!e.currentTarget.contains(e.relatedTarget as Node)) {
      setDraggedOverSubTask(null);
    }
  };

  const handleSubTaskDrop = (e: React.DragEvent, targetSubTaskId: string) => {
    e.preventDefault();
    e.stopPropagation();
    if (!draggedSubTask || draggedSubTask.id === targetSubTaskId) {
      setDraggedSubTask(null);
      setDraggedOverSubTask(null);
      return;
    }

    // Initialiser l'ordre si manquant
    const subTasksWithOrder = subTasks.map((st, index) => ({
      ...st,
      order: st.order !== undefined && st.order !== null ? st.order : index
    }));

    // Trier par ordre
    subTasksWithOrder.sort((a, b) => {
      const orderA = a.order !== undefined && a.order !== null ? a.order : 999999;
      const orderB = b.order !== undefined && b.order !== null ? b.order : 999999;
      return orderA - orderB;
    });

    // Trouver les indices
    const draggedIndex = subTasksWithOrder.findIndex(st => st.id === draggedSubTask.id);
    const targetIndex = subTasksWithOrder.findIndex(st => st.id === targetSubTaskId);

    if (draggedIndex === -1 || targetIndex === -1) {
      setDraggedSubTask(null);
      setDraggedOverSubTask(null);
      return;
    }

    // Réordonner
    const updatedSubTasks = [...subTasksWithOrder];
    const [removed] = updatedSubTasks.splice(draggedIndex, 1);
    updatedSubTasks.splice(targetIndex, 0, removed);

    // Mettre à jour les ordres
    updatedSubTasks.forEach((st, index) => {
      st.order = index;
    });

    setSubTasks(updatedSubTasks);
    setDraggedSubTask(null);
    setDraggedOverSubTask(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!title.trim()) {
      toast.error("Le titre est requis");
      return;
    }

    try {
      const tasksCollection = collection(db, "Taches");
      const currentUser = auth.currentUser;
      const now = Timestamp.now();

      if (initialData && initialData.uid) {
        // Mise à jour d'une tâche existante
        // Récupérer le document par uid
        const tasksSnapshot = await getDocs(tasksCollection);
        const taskDoc = tasksSnapshot.docs.find((d: QueryDocumentSnapshot) => {
          const data = d.data();
          return data.uid === initialData.uid || d.id === initialData.uid;
        });
        
        if (taskDoc) {
          const taskRef = doc(db, "Taches", taskDoc.id);
          // Utiliser UNIQUEMENT le statut pour compter les sous-tâches terminées
          const completedCount = subTasks.filter(st => st.status === 'Terminée').length;
          // Nettoyer les sous-tâches : s'assurer que completed correspond toujours au statut
          const cleanedSubTasks = subTasks.map(st => {
            const isCompleted = st.status === 'Terminée';
            return {
              id: st.id,
              title: st.title,
              completed: isCompleted, // completed = true SEULEMENT si status === 'Terminée'
              assignedTo: st.assignedTo || null,
              status: st.status || null,
              completedAt: isCompleted ? (st.completedAt || null) : null // null pour Firestore, sera converti en undefined dans l'interface
            };
          });
          
          await updateDoc(taskRef, {
            title,
            description: description || "",
            status,
            priority,
            assignedTo: assignedTo.trim() || null,
            dueDate: dueDate ? Timestamp.fromDate(new Date(dueDate)) : null,
            updatedAt: now,
            listId: initialData.listId || taskDoc.data().listId,
            subTasks: cleanedSubTasks,
            completedItems: completedCount,
            totalItems: subTasks.length,
            subTaskMode: subTaskMode
          });

          if (currentUser?.email) {
            await logUserAction(
              currentUser.email,
              "Modification tâche",
              `Tâche modifiée: ${title}`
            );
          }

          toast.success("Tâche modifiée avec succès");
        } else {
          toast.error("Tâche non trouvée");
        }
      } else {
        // Création d'une nouvelle tâche
        // Récupérer toutes les tâches pour générer le prochain ID
        const tasksSnapshot = await getDocs(tasksCollection);
        const allTasks = tasksSnapshot.docs.map(doc => doc.data() as Task);
        
        const maxId = allTasks.reduce((max: number, t: Task) => {
          const num = parseInt(t.id.replace(/\D/g, ''));
          return !isNaN(num) && num > max ? num : max;
        }, 0);
        const nextId = 'T' + String(maxId + 1).padStart(4, '0');

        // Utiliser UNIQUEMENT le statut pour compter les sous-tâches terminées
        const completedCount = subTasks.filter(st => st.status === 'Terminée').length;
        // Nettoyer les sous-tâches : s'assurer que completed correspond toujours au statut
        const cleanedSubTasks = subTasks.map(st => {
          const isCompleted = st.status === 'Terminée';
          return {
            id: st.id,
            title: st.title,
            completed: isCompleted, // completed = true SEULEMENT si status === 'Terminée'
            assignedTo: st.assignedTo || null,
            status: st.status || null,
            completedAt: isCompleted ? (st.completedAt || null) : null
          };
        });
        
        // Calculer l'ordre pour la nouvelle tâche dans sa liste
        const listId = initialData?.listId || null;
        const tasksInList = listId ? allTasks.filter((t: Task) => t.listId === listId) : [];
        const maxOrder = tasksInList.length > 0 
          ? Math.max(...tasksInList.map((t: Task) => t.order || 0))
          : -1;
        
        const newTask = {
          id: nextId,
          title,
          description: description || "",
          status,
          priority,
          assignedTo: assignedTo.trim() || null,
          dueDate: dueDate ? Timestamp.fromDate(new Date(dueDate)) : null,
          createdAt: now,
          updatedAt: now,
          uid: Date.now().toString() + Math.random().toString(36).substring(2, 8),
          listId: listId,
          order: maxOrder + 1,
          completed: false,
          completedItems: completedCount,
          totalItems: subTasks.length,
          subTasks: cleanedSubTasks,
          subTaskMode: subTaskMode
        };

        await addDoc(tasksCollection, newTask);

        if (currentUser?.email) {
          await logUserAction(
            currentUser.email,
            "Création tâche",
            `Tâche créée: ${title}`
          );
        }

        toast.success("Tâche créée avec succès");
      }

      handleClose();
    } catch (error) {
      console.error("Erreur lors de la sauvegarde de la tâche:", error);
      toast.error(`Erreur lors de la sauvegarde de la tâche: ${error instanceof Error ? error.message : String(error)}`);
    }
  };

  if (!show) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-content" style={{ borderRadius: '12px', display: 'flex', flexDirection: 'column', maxHeight: '90vh', padding: 0 }}>
        <div className="modal-header" style={{ backgroundColor: '#DFE6E9', borderRadius: '12px 12px 0 0', padding: '1.5rem', flexShrink: 0 }}>
          <h2 style={{ fontFamily: 'Montserrat, sans-serif', margin: 0, fontWeight: 'bold', color: '#424273' }}>{initialData && initialData.uid ? 'Modifier une tâche' : 'Ajouter une tâche'}</h2>
          <button className="modal-close" onClick={handleClose}>×</button>
        </div>
        
        <div style={{ overflowY: 'auto', flex: 1 }}>
          <div className="form-group inter-font" style={{ marginBottom: '1rem', paddingBottom: '1rem', borderBottom: '1px solid #e0e0e0', paddingLeft: '1.5rem', paddingRight: '1.5rem', paddingTop: '1.5rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <label style={{ fontSize: '0.9rem', margin: 0, fontWeight: '500' }}>Mode:</label>
            <select
              value={subTaskMode}
              onChange={(e) => {
                const newMode = e.target.value as "Collectif" | "Individuel";
                setSubTaskMode(newMode);
                // Si on passe en Individuel, assigner toutes les sous-tâches à la personne assignée
                if (newMode === "Individuel" && assignedTo.trim()) {
                  setSubTasks(subTasks.map(st => {
                    const status = st.status || "En attente";
                    return {
                      ...st,
                      assignedTo: assignedTo.trim(),
                      status: status, // Garder le statut existant ou mettre "En attente"
                      completed: status === 'Terminée' // Mettre à jour completed selon le statut
                    };
                  }));
                } else if (newMode === "Collectif") {
                  // Si on passe en Collectif, retirer l'assignation automatique et ajouter le statut
                  setSubTasks(subTasks.map(st => {
                    const status = st.status || "En attente";
                    return {
                      ...st,
                      assignedTo: undefined,
                      status: status,
                      completed: status === 'Terminée' // Mettre à jour completed selon le statut
                    };
                  }));
                }
              }}
              className="form-control inter-font"
              style={{ fontSize: '0.8rem', padding: '0.5rem 0.75rem', width: 'auto', minWidth: '120px' }}
            >
              <option value="Individuel">Individuel</option>
              <option value="Collectif">Collectif</option>
            </select>
          </div>
        </div>
        
        <form onSubmit={handleSubmit} className="modal-form" style={{ padding: '0 1.5rem 1.5rem 1.5rem', borderRadius: '0 0 12px 12px' }}>
          <div className="form-group inter-font">
            <label style={{ fontSize: '0.9rem' }}>Titre *</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="form-control inter-font"
              required
              placeholder="Titre de la tâche"
              style={{ fontSize: '0.8rem', padding: '0.5rem 0.75rem' }}
            />
          </div>

          <div className="form-group inter-font">
            <label style={{ fontSize: '0.9rem' }}>Description</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="form-control inter-font"
              rows={4}
              placeholder="Description de la tâche"
              style={{ fontSize: '0.8rem', padding: '0.5rem 0.75rem' }}
            />
          </div>

          {subTaskMode === "Individuel" && (
            <>
              <div style={{ display: 'flex', gap: '15px' }}>
                <div className="form-group inter-font" style={{ flex: 1 }}>
                  <label style={{ fontSize: '0.9rem' }}>Statut</label>
                  <select
                    value={status}
                    onChange={(e) => setStatus(e.target.value)}
                    className="form-control inter-font"
                    style={{ fontSize: '0.8rem', padding: '0.5rem 0.75rem' }}
                  >
                    <option value="En attente">En attente</option>
                    <option value="En cours">En cours</option>
                    <option value="Terminée">Terminée</option>
                  </select>
                </div>

                <div className="form-group inter-font" style={{ flex: 1 }}>
                  <label style={{ fontSize: '0.9rem' }}>Priorité</label>
                  <select
                    value={priority}
                    onChange={(e) => setPriority(e.target.value)}
                    className="form-control inter-font"
                    style={{ fontSize: '0.8rem', padding: '0.5rem 0.75rem' }}
                  >
                    <option value="Basse">Basse</option>
                    <option value="Moyenne">Moyenne</option>
                    <option value="Haute">Haute</option>
                  </select>
                </div>
              </div>

              <div className="form-group inter-font">
                <label style={{ fontSize: '0.9rem' }}>Assigné à</label>
                <select
                  value={assignedTo}
                  onChange={(e) => setAssignedTo(e.target.value)}
                  className="form-control inter-font"
                  style={{ fontSize: '0.8rem', padding: '0.5rem 0.75rem' }}
                >
                  <option value="">Sélectionner un utilisateur</option>
                  {adminUsers.map((admin) => {
                    const displayName = `${admin.firstName} ${admin.lastName}`.trim() || admin.email;
                    return (
                      <option key={admin.email} value={displayName}>
                        {displayName}
                      </option>
                    );
                  })}
                </select>
              </div>

              <div className="form-group inter-font">
                <label style={{ fontSize: '0.9rem' }}>Date d'échéance</label>
                <input
                  type="date"
                  value={dueDate}
                  onChange={(e) => setDueDate(e.target.value)}
                  className="form-control inter-font"
                  style={{ fontSize: '0.8rem', padding: '0.5rem 0.75rem' }}
                />
              </div>
            </>
          )}

          <div className="form-group inter-font">
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
              <button
                type="button"
                onClick={() => {
                  if (!showSubTaskTable) {
                    setShowSubTaskTable(true);
                  }
                  // Ajouter une nouvelle ligne vide
                  const newSubTask: SubTask = {
                    id: Date.now().toString() + Math.random().toString(36).substring(2, 8),
                    title: "",
                    completed: false, // "En attente" donc completed = false
                    assignedTo: subTaskMode === "Individuel" ? assignedTo.trim() || undefined : undefined,
                    status: "En attente" // Toujours définir un statut par défaut
                  };
                  setSubTasks([...subTasks, newSubTask]);
                }}
                style={{
                  background: '#007bff',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  width: '30px',
                  height: '30px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  fontSize: '1.2rem',
                  fontWeight: 'bold'
                }}
                title="Ajouter une sous-tâche"
              >
                +
              </button>
              <label style={{ fontSize: '0.9rem', margin: 0 }}>Sous-tâches</label>
            </div>
            
            {showSubTaskTable && (
              <div style={{ 
                border: '1px solid #ddd', 
                borderRadius: '4px', 
                padding: '8px',
                backgroundColor: '#f9f9f9',
                marginTop: '10px'
              }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ backgroundColor: '#e9ecef', borderBottom: '2px solid #ddd' }}>
                      <th style={{ padding: '8px', textAlign: 'left', fontSize: '0.75rem', fontWeight: '600', width: '30px' }}></th>
                      <th style={{ padding: '8px', textAlign: 'left', fontSize: '0.75rem', fontWeight: '600', width: '50px' }}>#</th>
                      <th style={{ padding: '8px', textAlign: 'left', fontSize: '0.75rem', fontWeight: '600' }}>Titre</th>
                      {subTaskMode === "Collectif" && (
                        <th style={{ padding: '8px', textAlign: 'left', fontSize: '0.75rem', fontWeight: '600', width: '150px' }}>Assigné à</th>
                      )}
                      <th style={{ padding: '8px', textAlign: 'left', fontSize: '0.75rem', fontWeight: '600', width: '140px' }}>Statut</th>
                      <th style={{ padding: '8px', textAlign: 'center', fontSize: '0.75rem', fontWeight: '600', width: '40px' }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {subTasks
                      .sort((a, b) => {
                        const orderA = a.order !== undefined && a.order !== null ? a.order : 999999;
                        const orderB = b.order !== undefined && b.order !== null ? b.order : 999999;
                        return orderA - orderB;
                      })
                      .map((subTask, index) => (
                      <tr 
                        key={subTask.id}
                        className="subtask-item"
                        data-subtask={subTask.id}
                        draggable
                        onDragStart={(e) => {
                          e.stopPropagation();
                          handleSubTaskDragStart(e, subTask);
                        }}
                        onDragOver={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleSubTaskDragOver(e, subTask.id);
                        }}
                        onDragLeave={handleSubTaskDragLeave}
                        onDrop={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleSubTaskDrop(e, subTask.id);
                        }}
                        style={{ 
                          borderBottom: '1px solid #e0e0e0',
                          cursor: 'grab',
                          opacity: draggedSubTask?.id === subTask.id ? 0.5 : 1,
                          backgroundColor: draggedOverSubTask === subTask.id ? '#f0f8ff' : 'transparent'
                        }}
                      >
                        <td style={{ padding: '8px', fontSize: '0.75rem', width: '30px' }}>
                          <i 
                            className="bi bi-grip-vertical" 
                            style={{ 
                              color: '#9ca3af',
                              fontSize: '14px',
                              cursor: 'grab'
                            }}
                            onMouseDown={(e) => e.stopPropagation()}
                            title="Glisser pour réordonner"
                          ></i>
                        </td>
                        <td style={{ padding: '8px', fontSize: '0.75rem', textAlign: 'center' }}>{index + 1}</td>
                        <td style={{ padding: '8px' }}>
                          <input
                            type="text"
                            value={subTask.title}
                            onChange={(e) => updateSubTaskTitle(subTask.id, e.target.value)}
                            className="form-control inter-font"
                            style={{ 
                              fontSize: '0.75rem',
                              padding: '0.4rem 0.6rem',
                              border: '1px solid #ddd',
                              borderRadius: '4px',
                              width: '100%'
                            }}
                            placeholder="Titre de la sous-tâche"
                          />
                        </td>
                        {subTaskMode === "Collectif" && (
                          <td style={{ padding: '8px' }}>
                            <select
                              value={subTask.assignedTo || ""}
                              onChange={(e) => updateSubTaskAssignedTo(subTask.id, e.target.value)}
                              className="form-control inter-font"
                              style={{ 
                                fontSize: '0.75rem',
                                padding: '0.4rem 0.6rem',
                                border: '1px solid #ddd',
                                borderRadius: '4px',
                                width: '100%'
                              }}
                            >
                              <option value="">Sélectionner un utilisateur</option>
                              {adminUsers.map((admin) => {
                                const displayName = `${admin.firstName} ${admin.lastName}`.trim() || admin.email;
                                return (
                                  <option key={admin.email} value={displayName}>
                                    {displayName}
                                  </option>
                                );
                              })}
                            </select>
                          </td>
                        )}
                        <td style={{ padding: '8px' }}>
                          <select
                            value={subTask.status || "En attente"}
                            onChange={(e) => updateSubTaskStatus(subTask.id, e.target.value)}
                            className="form-control inter-font"
                            style={{ 
                              fontSize: '0.75rem',
                              padding: '0.4rem 0.6rem',
                              border: '1px solid #ddd',
                              borderRadius: '4px',
                              width: '100%'
                            }}
                          >
                            <option value="En attente">En attente</option>
                            <option value="En cours">En cours</option>
                            <option value="Terminée">Terminée</option>
                          </select>
                        </td>
                        <td style={{ padding: '8px', width: '40px', textAlign: 'center' }}>
                          <button
                            type="button"
                            onClick={() => deleteSubTask(subTask.id)}
                            style={{
                              background: 'none',
                              border: 'none',
                              color: '#dc3545',
                              cursor: 'pointer',
                              padding: '4px 8px',
                              fontSize: '0.9rem',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}
                            title="Supprimer"
                          >
                            <i className="bi bi-trash"></i>
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <div className="modal-actions">
            <button type="button" onClick={handleClose} className="cancel-button inter-font" style={{ fontSize: '0.8rem' }}>
              Annuler
            </button>
            <button type="submit" className="submit-button inter-font" style={{ fontSize: '0.8rem' }}>
              {initialData && initialData.uid ? "Mettre à jour" : "Créer"}
            </button>
          </div>
        </form>
        </div>
      </div>
    </div>
  );
};

export default TaskModal;

